import { CLINICAL_STUDY, REGION_VARIANT } from 'constants/featureFlags';
import {
  availableTechnology,
  PRODUCT_VERSIONS,
  TECHNOLOGIES,
  TECHNOLOGIES_BY_GROUP,
  TECHNOLOGY_GROUPS,
  TTechnologiesAndGroups,
  TTechnology,
  TTechnologyGroups,
} from 'constants/products';
import { REGION_VARIANTS, TRegionVariant } from 'constants/region';
import { logError } from 'utils/logError';
import { TECHNOLOGY_PROPERTIES } from './setupProducts';

/**
 * Checks if the provided string is a valid TTechnology.
 * @param envTechnology - The technology string to check.
 * @returns True if the string is a valid TTechnology, false otherwise.
 */
const isTechnology = (envTechnology?: string): envTechnology is TTechnology => {
  return (
    envTechnology !== undefined &&
    Object.values(TECHNOLOGIES).includes(envTechnology as TTechnology)
  );
};

/**
 * Type guard to check if a key is a TTechnologyGroups.
 */
const isTechnologyGroups = (
  key: TTechnologiesAndGroups,
): key is TTechnologyGroups =>
  Object.values<TTechnologiesAndGroups>(TECHNOLOGY_GROUPS).includes(key);

/**
 * Class representing the available technology product and related utilities.
 */
class TechnologyProduct {
  /**
   * The currently available technology.
   */
  public availableTechnology: TTechnology;

  /**
   * The region variant for the technology.
   */
  public regionVariant: TRegionVariant;

  /**
   * Indicates if the product is part of a clinical study.
   */
  public isClinicalStudy: boolean;

  /**
   * The technology (deepCapsule, smartCholangio, etc) version.
   */
  public version: string | undefined;

  /**
   * Constructs a TechnologyProduct instance and validates the available technology.
   * @throws {Error} If the available technology is not valid.
   */
  constructor() {
    if (!isTechnology(availableTechnology)) {
      logError({
        error: new Error(
          `Unknown technology "${typeof availableTechnology == 'string' ? availableTechnology : 'undefined'}" in TechnologyManager`,
        ),
      });
      throw new Error('Wrong environment variable for technology');
    }
    this.regionVariant = REGION_VARIANT;
    this.isClinicalStudy = CLINICAL_STUDY;
    this.availableTechnology = availableTechnology;
    this.version = PRODUCT_VERSIONS[availableTechnology];
  }

  /**
   * Checks if the provided technology or any in the array is the available technology.
   * @param technology - A technology or array of technologies to check.
   * @returns True if the available technology matches any in the input, false otherwise.
   */
  public isAvailable = (technology: TTechnology | TTechnology[]): boolean => {
    const technologiesToCheck = Array.isArray(technology)
      ? technology
      : [technology];
    return technologiesToCheck.some((t) => t === this.availableTechnology);
  };

  /**
   * Filters the provided component keys to return only technology groups (detection or pancreas).
   * @param technologiesPossibilities - The component keys to filter.
   * @returns An array of technology groups or undefined if none found.
   */
  public getTechnologyGroups = (
    technologiesPossibilities: TTechnologiesAndGroups[],
  ): TTechnologyGroups[] => {
    return technologiesPossibilities.filter(isTechnologyGroups);
  };

  /**
   * Finds a technology group key.
   * @param technologiesGroup - The array of technology groups to search.
   * @returns A group key the available technology belongs to.
   */
  public findAvailableTechnologyGroupKey = (
    technologiesGroup: TTechnologyGroups[],
  ) => {
    return technologiesGroup.find((group) => {
      return TECHNOLOGIES_BY_GROUP[group].includes(this.availableTechnology);
    });
  };

  /**
   * Checks if the region variant set is US
   * @returns True if the region variant is US, false otherwise.
   */
  public isRegionVariantUS = () => {
    return this.regionVariant === REGION_VARIANTS.US;
  };

  public getProperties = () => {
    if (this.isAvailable(TECHNOLOGIES_BY_GROUP.capsule)) {
      return TECHNOLOGY_PROPERTIES[
        `${this.availableTechnology}_${this.regionVariant}`
      ];
    }
    return TECHNOLOGY_PROPERTIES[this.availableTechnology];
  };
}

export { TechnologyProduct };
export default new TechnologyProduct();
